import { useCallback, useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { callingService } from '../services/callingService';
import { connectionsApi } from '../services/connectionsApi';
import type {
  Connection,
  PresenceSelfEvent,
  PresenceSnapshotEvent,
  PresenceUpdateEvent,
} from '../types/connections';

type PresenceSnapshot = {
  signalingConnected: boolean;
  selfActive: boolean;
  byUserId: Map<number, boolean>;
};

let presenceByUserId = new Map<number, boolean>();
let signalingConnected = false;
/** How accepted connections currently see you. */
let selfActive = true;
let boundSocket: Socket | null = null;
const subscribers = new Set<() => void>();

function emitChange() {
  subscribers.forEach((listener) => listener());
}

function setSelfActive(active: boolean) {
  if (selfActive === active) return;
  selfActive = active;
  emitChange();
}

function setPeerActive(userId: number, active: boolean) {
  const previous = presenceByUserId.get(userId);
  if (previous === active) return;
  presenceByUserId.set(userId, active);
  emitChange();
}

function applySnapshot(event: PresenceSnapshotEvent) {
  const next = new Map(presenceByUserId);
  (event.peers ?? []).forEach((peer) => {
    next.set(peer.userId, peer.active);
  });
  presenceByUserId = next;
  if (event.self && typeof event.self.active === 'boolean') {
    selfActive = event.self.active;
  }
  emitChange();
}

function getSnapshot(): PresenceSnapshot {
  return {
    signalingConnected,
    selfActive,
    byUserId: new Map(presenceByUserId),
  };
}

function bindSocketHandlers() {
  const socket = callingService.getSocket();
  if (!socket || boundSocket === socket) return;
  boundSocket = socket;

  const onConnect = () => {
    signalingConnected = true;
    emitChange();
    callingService.listPresence();
    void connectionsApi
      .getPresence()
      .then((status) => setSelfActive(Boolean(status.active)))
      .catch(() => undefined);
  };
  const onDisconnect = () => {
    signalingConnected = false;
    emitChange();
  };
  const onSnapshot = (event: PresenceSnapshotEvent) => {
    applySnapshot(event);
  };
  const onUpdate = (event: PresenceUpdateEvent) => {
    if (event?.userId == null) return;
    setPeerActive(event.userId, Boolean(event.active));
  };
  const onSelf = (event: PresenceSelfEvent) => {
    if (typeof event?.active !== 'boolean') return;
    setSelfActive(event.active);
  };

  socket.on('connect', onConnect);
  socket.on('disconnect', onDisconnect);
  socket.on('presence:snapshot', onSnapshot);
  socket.on('presence:update', onUpdate);
  socket.on('presence:self', onSelf);

  if (socket.connected) onConnect();
}

function clearPresenceState() {
  presenceByUserId = new Map();
  signalingConnected = false;
  selfActive = true;
  boundSocket = null;
  emitChange();
}

export function ensurePresenceConnected(token = localStorage.getItem('token')) {
  if (!token) return null;
  const socket = callingService.connect(token);
  bindSocketHandlers();
  return socket;
}

export function disconnectPresence() {
  callingService.disconnect();
  clearPresenceState();
}

export function seedPresenceFromConnections(
  accepted: Connection[],
  currentUserId: number | null,
) {
  if (currentUserId == null) return;
  let changed = false;
  accepted.forEach((connection) => {
    if (typeof connection.isActive !== 'boolean') return;
    const peerId =
      connection.initiatingUserId === currentUserId
        ? connection.acceptingUserId
        : connection.initiatingUserId;
    if (peerId === currentUserId) return;
    if (presenceByUserId.get(peerId) === connection.isActive) return;
    presenceByUserId.set(peerId, connection.isActive);
    changed = true;
  });
  if (changed) emitChange();
}

export function usePresence() {
  const [snapshot, setSnapshot] = useState<PresenceSnapshot>(getSnapshot);
  const [updatingSelf, setUpdatingSelf] = useState(false);

  useEffect(() => {
    const sync = () => setSnapshot(getSnapshot());
    subscribers.add(sync);
    ensurePresenceConnected();
    sync();
    return () => {
      subscribers.delete(sync);
    };
  }, []);

  const isPeerActive = useCallback(
    (userId: number) => snapshot.byUserId.get(userId) === true,
    [snapshot.byUserId],
  );

  const refreshPresence = useCallback(() => {
    ensurePresenceConnected();
    callingService.listPresence();
    void connectionsApi
      .getPresence()
      .then((status) => setSelfActive(Boolean(status.active)))
      .catch(() => undefined);
  }, []);

  const setAppearActive = useCallback(async (active: boolean) => {
    setUpdatingSelf(true);
    // Optimistic UI; presence:self / REST response confirm.
    setSelfActive(active);
    try {
      ensurePresenceConnected();
      if (callingService.getSocket()?.connected) {
        callingService.setPresence(active);
      } else {
        const status = await connectionsApi.setPresence(active);
        setSelfActive(Boolean(status.active));
      }
    } catch {
      // Re-sync from server if the preference change failed.
      try {
        const status = await connectionsApi.getPresence();
        setSelfActive(Boolean(status.active));
      } catch {
        // ignore
      }
      throw new Error('Failed to update presence preference');
    } finally {
      setUpdatingSelf(false);
    }
  }, []);

  return {
    signalingConnected: snapshot.signalingConnected,
    selfActive: snapshot.selfActive,
    presenceByUserId: snapshot.byUserId,
    isPeerActive,
    refreshPresence,
    setAppearActive,
    updatingSelf,
  };
}
